import React, { useEffect, useRef, useCallback } from 'react';
import s from './NewProcess.module.css';

import p1 from '../images/בית של אור ועיצוב 1.webp';
import p2 from '../images/בית של יוקרה וחומר 3.webp';
import p3 from '../images/בית של יוקרה וחומר 2.webp';
import p4 from '../images/בית של קוביות ועץ 1.webp';
import p5 from '../images/בית של עץ ואור 3.webp';

const STAGES = [
  {
    num: '01',
    title: 'פגישה ומיפוי החלום',
    desc: 'נפגשים אצלכם או בסטודיו, עולים לאתר ומקשיבים לאיך שאתם חיים. מתוך השיחה עולות שלוש חלופות תכנון עם הדמיה תלת ממדית - ורק אז בוחרים.',
    img: p1,
  },
  {
    num: '02',
    title: 'היתר בנייה, בלי כאב ראש',
    desc: 'גרמושקה לפי מפרט העירייה, פתיחת תיק, תיאום יועצים ומעקב עד קבלת ההיתר. אתם לא רואים את הבירוקרטיה - רק את התוצאה.',
    img: p2,
  },
  {
    num: '03',
    title: 'תכניות ביצוע, עד לסנטימטר',
    desc: 'סט תכניות 1:50 מפורט - בנייה, חשמל, תאורה, אלומיניום ונגרות. כל פרט מסומן מראש, כך שהקבלן יודע בדיוק מה לבנות.',
    img: p3,
  },
  {
    num: '04',
    title: 'פיקוח עליון, לידכם באתר',
    desc: 'מגיעים לאתר, חותמים על מה שצריך, מוודאים שמה שסימנו על הנייר - בדיוק מה שנבנה. ליווי עד טופס 4.',
    img: p4,
  },
  {
    num: '05',
    title: 'עיצוב פנים, הנשמה של הבית',
    desc: 'קונספט, ריהוט, תאורה, חיפויים וריצוף - הכל נבחר בטוב טעם, מתואם עם התכנון, ובליווי מול הספקים.',
    img: p5,
  },
];

const TOTAL = String(STAGES.length).padStart(2, '0');

export default function NewProcess() {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);
  const lineFillRef = useRef(null);
  const timelineRef = useRef(null);

  /* ---- Intersection Observer for card reveals ---- */
  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(s.cardVisible);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    const cards = cardsRef.current.filter(Boolean);
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  /* ---- Scroll-driven progress line ---- */
  const handleScroll = useCallback(() => {
    const timeline = timelineRef.current;
    const fill = lineFillRef.current;
    if (!timeline || !fill) return;

    const rect = timeline.getBoundingClientRect();
    const viewH = window.innerHeight;

    // Calculate how far through the timeline we've scrolled
    const start = rect.top - viewH;
    const end = rect.bottom;
    const total = end - start;
    const scrolled = -start;
    const pct = Math.min(Math.max(scrolled / total, 0), 1) * 100;

    fill.style.height = `${pct}%`;
  }, []);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <section className={s.section} ref={sectionRef}>
      <div className={s.grain} aria-hidden="true" />

      {/* ---- Header ---- */}
      <header className={s.header}>
        <span className={s.eyebrow}>&mdash; התהליך &mdash;</span>
        <h2 className={s.title}>חמישה שלבים לבית שלכם</h2>
        <p className={s.lead}>
          מהפגישה הראשונה ועד שפותחים את הדלת - בסדר שקוף וברור.
        </p>
      </header>

      {/* ---- Timeline ---- */}
      <div className={s.timeline} ref={timelineRef}>
        {/* Vertical progress line */}
        <div className={s.progressLine} aria-hidden="true">
          <div className={s.progressLineFill} ref={lineFillRef} />
        </div>

        {STAGES.map((stage, i) => (
          <article
            key={stage.num}
            className={s.card}
            ref={(el) => { cardsRef.current[i] = el; }}
          >
            {/* Watermark */}
            <span className={s.watermark} aria-hidden="true">
              {stage.num}
            </span>

            {/* Image */}
            <div className={s.imageWrap}>
              <img
                className={s.image}
                src={stage.img}
                alt={stage.title}
                loading="lazy"
                decoding="async"
              />
            </div>

            {/* Text */}
            <div className={s.textWrap}>
              <span className={s.stepIndicator}>
                שלב {stage.num} / {TOTAL}
              </span>
              <span className={s.stepNumber}>{stage.num}</span>
              <h3 className={s.stepTitle}>{stage.title}</h3>
              <p className={s.stepDesc}>{stage.desc}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
